import { z } from 'zod';
import { NumberProvider } from '../enums.js';
import { E164, TimestampSchema } from '../primitives.js';
/**
 * `divertSources/{ISO-NSN}` — the reverse index that lets ONE inbound number
 * serve many businesses (doc 18).
 *
 * Keyed by the business's **own line** reduced to a country-scoped national
 * significant number (`PL-600200300`, see `telephony/nsn.ts`), because that is
 * what the carrier hands us in the `Diversion` header when a call is diverted.
 * Server-only in both directions, exactly like `phoneNumbers`.
 *
 * A document here is a **routing grant**, and it is created only by a
 * verification call that proved possession of the divert — never by an owner
 * typing a number into a form. The declared number on the business doc is only
 * an expectation to match against; if declaring were enough, anyone could enter
 * a competitor's number and receive their calls (doc 18 D2).
 */
export const DivertSourceSchema = z.object({
    businessId: z.string().min(1),
    /** The declared line in E.164, for display and support. The routing key is
     * the document id; this is the human-readable form of the same fact. */
    sourceNumber: E164,
    /** Which of our numbers the divert lands on — the `To` we expect. Lets a pool
     * number be retired by finding every source pointing at it. */
    poolNumber: E164,
    /** Server timestamp of the verification call that granted this. Never null:
     * a grant cannot exist without one. */
    verifiedAt: TimestampSchema,
    /** The `ForwardedFrom` exactly as the carrier sent it, kept for forensics.
     * When a carrier changes format under us this is the only record of what the
     * old one looked like. */
    rawForwardedFrom: z.string(),
});
/**
 * `unattributedCalls/{autoId}` — a call we could not bind to any business.
 *
 * By construction this should be empty: nobody dials our inbound numbers
 * directly (they are published nowhere), so a call arriving on one is always a
 * divert from a business we know. An entry here therefore means a **real
 * customer of a paying business reached us and we could not tell whose they
 * were** — which is why it is captured with the raw header rather than merely
 * counted, and why it is logged at error severity (doc 18 §6.5).
 *
 * Two jobs: alerting now, and re-attribution later — once the shape is
 * understood, these rows say which business to apologise to.
 */
export const UNATTRIBUTED_REASONS = [
    /** The carrier sent no diverted-from at all. Under the shared-number model
     * this is the common failure, not an edge case: the business exists and its
     * divert works, the operator just doesn't say where the call came from. */
    'carrier_stripped',
    /** A diverted-from arrived but could not be reduced to a routing key — a
     * shape `toNsn` does not know. The raw value is the input to fixing that. */
    'unknown_format',
    /** Reduced cleanly, but no business has proved that line: never verified,
     * verified then re-declared, or archived. */
    'unclaimed_line',
];
export const UnattributedReason = z.enum(UNATTRIBUTED_REASONS);
export const UnattributedCallSchema = z.object({
    /** Which of the three faults this was. Recorded rather than derived at read
     * time so the report can group without re-parsing every raw value. */
    reason: UnattributedReason,
    /** The `ForwardedFrom` verbatim, or null when the carrier sent none at all. */
    rawForwardedFrom: z.string().nullable(),
    /** The routing key we derived, when we managed to derive one. Null means the
     * value could not be reduced; non-null means it reduced fine but no business
     * has claimed it — a materially different problem. */
    derivedKey: z.string().nullable(),
    /** Our number the call arrived on. */
    to: z.string(),
    /** True when that number is a SHARED one. A miss here is materially worse
     * than a miss on an unassigned number: somebody's paying customer was on the
     * line, whereas an unassigned number is usually just a wrong number. */
    toIsPool: z.boolean().default(false),
    /** The caller, so the business can be told who to ring back. */
    from: z.string().nullable(),
    provider: NumberProvider,
    /** Other SIP headers present on the call — catches a carrier that put the
     * divert somewhere other than `ForwardedFrom` (e.g. `History-Info`). */
    sipHeaderKeys: z.array(z.string()).default([]),
    callSid: z.string(),
    occurredAt: TimestampSchema,
});
//# sourceMappingURL=divertSource.js.map